import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, Lock, User, Gift } from "lucide-react";
import PasswordStrength from "@/components/travel/PasswordStrength";
import { api } from "@/lib/api";

export default function Login() {
  const [sp] = useSearchParams();
  const refParam = sp.get("ref") || "";
  const [mode, setMode] = useState(refParam ? "signup" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await signIn(email, password);
      else {
        // Custom signup call with ref
        const r = await api.post("/auth/register", { email, password, name, ref: refParam || null });
        localStorage.setItem("tb_token", r.data.token);
        window.dispatchEvent(new Event("storage")); // hint context reload
      }
      toast.success("Bienvenue !");
      nav("/dashboard");
      setTimeout(()=>window.location.reload(), 200); // ensure auth context refresh
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="glass-strong rounded-2xl p-6">
        <h1 className="font-display text-2xl font-bold mb-1">{mode === "login" ? "Connexion" : "Créer un compte"}</h1>
        <p className="text-sm text-slate-400 mb-6">{mode === "login" ? "Retrouvez vos voyages et contacts." : "Rejoignez l'annuaire des voyageurs."}</p>
        {refParam && mode === "signup" && (
          <div data-testid="ref-banner" className="mb-4 p-3 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/40 text-xs text-[#F59E0B] flex items-center gap-2">
            <Gift size={14}/> Vous rejoignez via un lien de parrainage — votre parrain gagne 1 déblocage gratuit.
          </div>
        )}

        <button
          data-testid="auth-google-btn"
          onClick={googleLogin}
          className="w-full btn-ghost-glass py-2.5 rounded-lg text-sm mb-4 flex items-center justify-center gap-2"
        >
          <svg viewBox="0 0 24 24" width="16" height="16"><path fill="#EA4335" d="M12 5c1.6 0 3 .6 4.1 1.5l3-3C17.2 1.7 14.7.7 12 .7 7.3.7 3.3 3.4 1.4 7.3l3.5 2.7C5.9 7 8.7 5 12 5z"/><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.1 2.7-2.4 3.6l3.7 2.9c2.2-2 3.7-5 3.7-8.7z"/><path fill="#FBBC05" d="M4.9 14c-.2-.6-.3-1.3-.3-2s.1-1.4.3-2L1.4 7.3C.5 8.7 0 10.3 0 12s.5 3.3 1.4 4.7L4.9 14z"/><path fill="#34A853" d="M12 23.3c3 0 5.6-1 7.4-2.7l-3.7-2.9c-1 .7-2.3 1.1-3.7 1.1-3.3 0-6.1-2-7-4.8L1.4 16.7C3.3 20.6 7.3 23.3 12 23.3z"/></svg>
          Continuer avec Google
        </button>

        <button
          data-testid="auth-apple-btn"
          onClick={() => toast.info("Apple Sign-In : fournissez vos credentials Apple Developer (Team ID, Key ID, Service ID, .p8) et je l'active en 5 min.")}
          className="w-full btn-ghost-glass py-2.5 rounded-lg text-sm mb-4 flex items-center justify-center gap-2"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M16.365 1.43c0 1.14-.417 2.16-1.25 3.05-.83.87-1.86 1.4-2.98 1.29-.13-1.12.44-2.27 1.24-3.1.87-.89 2.11-1.44 2.99-1.24zM21 17.38c-.53 1.22-1.16 2.36-1.92 3.4-1.05 1.44-1.9 2.44-2.55 3.02-.98.89-2.03 1.36-3.15 1.4-.79 0-1.75-.23-2.86-.68-1.11-.45-2.13-.68-3.06-.68-.98 0-2.03.23-3.14.68-1.11.46-2.01.7-2.7.72-1.06.05-2.13-.43-3.19-1.44C.87 22.39.14 20.3.14 17.57c0-1.66.36-3.09 1.08-4.29a5.86 5.86 0 0 1 2.15-2.14 5.79 5.79 0 0 1 2.9-.81c.84 0 1.94.26 3.31.77 1.36.51 2.24.77 2.63.77.29 0 1.26-.3 2.9-.9 1.54-.55 2.84-.78 3.9-.68 2.87.23 5.02 1.38 6.44 3.44-2.56 1.55-3.83 3.72-3.8 6.5.03 2.16.81 3.96 2.35 5.4z"/></svg>
          Continuer avec Apple
        </button>

        <div className="flex items-center gap-3 my-4 text-xs text-slate-500">
          <div className="flex-1 h-px bg-slate-800"/>ou<div className="flex-1 h-px bg-slate-800"/>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 focus-within:border-[#FF6B6B]">
              <User size={16} className="text-slate-400" />
              <input data-testid="auth-name" required value={name} onChange={(e)=>setName(e.target.value)} placeholder="Votre nom" className="bg-transparent flex-1 outline-none text-sm" />
            </div>
          )}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 focus-within:border-[#FF6B6B]">
            <Mail size={16} className="text-slate-400" />
            <input data-testid="auth-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="Email" className="bg-transparent flex-1 outline-none text-sm" />
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 focus-within:border-[#FF6B6B]">
            <Lock size={16} className="text-slate-400" />
            <input data-testid="auth-password" type="password" required minLength={6} value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="Mot de passe (6+ car.)" className="bg-transparent flex-1 outline-none text-sm" />
          </div>
          {mode === "signup" && <PasswordStrength value={password} />}
          <button data-testid="auth-submit" disabled={loading} className="w-full btn-coral py-2.5 rounded-lg text-sm">{loading ? "…" : (mode === "login" ? "Se connecter" : "Créer mon compte")}</button>
          {mode === "login" && (
            <div className="text-right">
              <Link to="/forgot-password" data-testid="auth-forgot-link" className="text-xs text-slate-400 hover:text-[#FF6B6B]">Mot de passe oublié ?</Link>
            </div>
          )}
        </form>

        <p className="text-center text-xs text-slate-400 mt-5">
          {mode === "login" ? (
            <>Pas encore de compte ? <button data-testid="auth-switch-signup" onClick={()=>setMode("signup")} className="text-[#FF6B6B] hover:underline">Inscription</button></>
          ) : (
            <>Déjà inscrit ? <button data-testid="auth-switch-login" onClick={()=>setMode("login")} className="text-[#FF6B6B] hover:underline">Connexion</button></>
          )}
        </p>
        <p className="text-center text-[11px] text-slate-500 mt-3">
          Démo admin : <Link to="/login" className="text-slate-400">admin@travelbuddy.demo / Admin1234!</Link>
        </p>
        <p className="text-center text-[11px] text-slate-500">
          Owner : <span className="text-slate-400">boutouhassan@gmail.com / Owner1234!</span>
        </p>
      </div>
    </div>
  );
}
